/**
 * Catalogue price review — the central price group's policy price beside
 * what customers actually paid for the equivalent spec, per item × quantity
 * (price-precedent brief J5). The evidence surface for the commercial review
 * that keeps `catalogue_customer_quoting` closed in production: sorted by
 * distance from precedent, so the review is a top-down list, not a hunt.
 *
 * Read-only by design. The outcome of a review is a decision per item — a
 * price group edit, or opening the flag — never a write from this page.
 *
 * Most rows have no evidence yet, and the page says why per row rather than
 * implying no sales: the cross-customer spread is keyed on the structural
 * spec key, which few catalogue items resolve today (`unkeyed`), and a keyed
 * spec with a thin sample is withheld (`under_min_orders`).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  CataloguePriceReview,
  CataloguePriceReviewRow,
} from "@shared-core/data/price-precedent-types";
import {
  formatPrecedentDate,
  formatSignedPct,
} from "@shared-core/domain/price-precedent";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { Input } from "../app/components/primitives/input";
import { Label } from "../app/components/primitives/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";

type State =
  | { kind: "loading" }
  | { kind: "error" }
  | { kind: "ready"; review: CataloguePriceReview };

/** Flag a policy price that sits well outside what the market has borne. */
function deltaTone(pct: number | null): string {
  if (pct == null) return "text-muted-foreground";
  if (Math.abs(pct) < 5) return "";
  return pct > 0
    ? "text-amber-700 dark:text-amber-400"
    : "text-sky-700 dark:text-sky-400";
}

function ReviewRow({ row }: { row: CataloguePriceReviewRow }) {
  const p = row.precedent;
  return (
    <TableRow>
      <TableCell>
        <Link
          to={row.item_url}
          className="font-medium text-primary hover:underline"
        >
          {row.item_name}
        </Link>
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {row.quantity.toLocaleString()}
      </TableCell>
      <TableCell className="text-right tabular-nums font-medium">
        {formatQuotedPrice(row.policy_pence)}
      </TableCell>
      {p ? (
        <>
          <TableCell className="text-right tabular-nums text-muted-foreground">
            {formatQuotedPrice(p.p25_pence)}
          </TableCell>
          <TableCell className="text-right tabular-nums">
            {formatQuotedPrice(p.median_pence)}
          </TableCell>
          <TableCell className="text-right tabular-nums text-muted-foreground">
            {formatQuotedPrice(p.p75_pence)}
          </TableCell>
          <TableCell className="text-right tabular-nums text-muted-foreground">
            {row.orders}
          </TableCell>
          <TableCell
            className={`text-right tabular-nums font-medium ${deltaTone(row.delta_vs_median_pct)}`}
          >
            {row.delta_vs_median_pct == null
              ? "—"
              : formatSignedPct(row.delta_vs_median_pct, 0)}
          </TableCell>
        </>
      ) : (
        <TableCell colSpan={5} className="text-xs text-muted-foreground">
          {row.precedent_coverage === "under_min_orders"
            ? `${row.orders} ${row.orders === 1 ? "order" : "orders"} in the window — too few to compare`
            : "Spec can't be keyed yet — no cross-customer comparison"}
        </TableCell>
      )}
    </TableRow>
  );
}

/** How much of the list has evidence behind it — so an admin reading the
 *  top of the table knows how far down the comparison actually goes. */
function ReviewCoverage({ review }: { review: CataloguePriceReview }) {
  const recorded = review.rows.filter(
    (r) => r.precedent_coverage === "recorded",
  ).length;
  const unkeyed = review.rows.filter(
    (r) => r.precedent_coverage === "unkeyed",
  ).length;
  return (
    <p className="text-xs text-muted-foreground">
      {recorded} of {review.rows.length} rows have sold evidence
      {unkeyed > 0 && ` · ${unkeyed} can't be compared yet (spec not keyable)`}
      {review.uncosted_rung_count > 0 &&
        ` · ${review.uncosted_rung_count} quantity breaks not costed at this group, left out`}
    </p>
  );
}

export function SettingsPricingReviewPage() {
  const adapter = useDataAdapter();
  const [minOrders, setMinOrders] = useState(5);
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    adapter
      .getCataloguePriceReview({ minOrders })
      .then((review) => {
        if (!cancelled) setState({ kind: "ready", review });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, minOrders]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Price review</h2>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Each catalogue item&rsquo;s policy price at the central price group,
            beside what customers actually paid for the equivalent spec. Sorted
            by distance from the median. Precedent is sold jobs across all
            customers; it is not a customer&rsquo;s own history.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="min-orders" className="text-sm text-muted-foreground">
            Min orders
          </Label>
          <Input
            id="min-orders"
            type="number"
            min={1}
            className="w-20"
            value={minOrders}
            onChange={(e) =>
              setMinOrders(Math.max(1, parseInt(e.target.value, 10) || 1))
            }
          />
        </div>
      </div>

      {state.kind === "loading" && (
        <p className="text-sm text-muted-foreground">
          Loading the price review…
        </p>
      )}
      {state.kind === "error" && (
        <p role="alert" className="text-sm text-muted-foreground">
          Couldn&rsquo;t load the price review. Try again shortly.
        </p>
      )}
      {state.kind === "ready" && (
        <>
          <div className="text-xs text-muted-foreground">
            {state.review.price_group_name}
            {state.review.version != null && ` · v${state.review.version}`} ·
            precedent from the last {state.review.window_months} months ·{" "}
            {state.review.refreshed_at
              ? `refreshed ${formatPrecedentDate(state.review.refreshed_at)}`
              : "refresh time unknown"}
            {" · "}
            <Link
              to={paths.settingsPricing()}
              className="text-primary hover:underline"
            >
              edit price groups
            </Link>
          </div>
          <ReviewCoverage review={state.review} />
          <div className="overflow-x-auto rounded-md border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Policy</TableHead>
                  <TableHead className="text-right">p25</TableHead>
                  <TableHead className="text-right">Median</TableHead>
                  <TableHead className="text-right">p75</TableHead>
                  <TableHead className="text-right">Orders</TableHead>
                  <TableHead className="text-right">Δ vs median</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {state.review.rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-sm text-muted-foreground"
                    >
                      No core range item is priced at this group yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  state.review.rows.map((row) => (
                    <ReviewRow
                      key={`${row.item_id}-${row.quantity}`}
                      row={row}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
